/**
 * recurringSuggestions.js — thin client for /api/recurring/suggestions
 *
 * Phase 70C — Auto-detected recurring streams.
 *
 * The backend detects likely recurring streams from the user's transactions
 * (no auto-promotion — every suggestion needs explicit Accept). This module
 * is the only place the frontend talks to that API so component code stays
 * focused on render / interaction.
 *
 * Token discipline: we fetch a fresh Supabase access token at call time via
 * `supabase.auth.getSession()`. See useParseJobs.js for the back-story on
 * why hook-scoped closures can't be trusted with tokens.
 */
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function freshToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

async function authHeaders() {
  const t = await freshToken();
  if (!t) throw new Error('Not signed in.');
  return { Authorization: `Bearer ${t}` };
}

/**
 * GET /api/recurring/suggestions — list pending suggestions for the user.
 * Returns { suggestions: [...], last_scan_at: string|null }.
 *
 * Never throws — returns { suggestions: [] } if the backend is unreachable.
 * The Recurring page treats "no suggestions" the same as "empty" so a
 * transient network blip never blocks the rest of the UI.
 */
export async function listSuggestions() {
  try {
    const headers = await authHeaders();
    const r = await axios.get(`${BACKEND_URL}/api/recurring/suggestions`, { headers, timeout: 15000 });
    return {
      suggestions: Array.isArray(r.data?.suggestions) ? r.data.suggestions : [],
      last_scan_at: r.data?.last_scan_at || null,
    };
  } catch (e) {
    // Phase 70C — the SQL migration may not have been applied yet. We
    // detect that by a 500 with the table-missing hint OR a 404 if the
    // route file fails to import. Either way, surface gracefully.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[recurring/suggestions] list failed:', e?.response?.status, e?.message);
    }
    return { suggestions: [], last_scan_at: null };
  }
}

/**
 * POST /api/recurring/suggestions/scan — re-run the detector on demand.
 * Returns the refreshed { suggestions, last_scan_at }.
 *
 * Idempotent: the partial-unique index keeps the upserts in lock-step with
 * the latest detection, so calling this twice in a row is safe and cheap.
 */
export async function scanForSuggestions() {
  const headers = await authHeaders();
  const r = await axios.post(`${BACKEND_URL}/api/recurring/suggestions/scan`, {}, { headers, timeout: 30000 });
  return {
    suggestions: Array.isArray(r.data?.suggestions) ? r.data.suggestions : [],
    last_scan_at: r.data?.last_scan_at || null,
  };
}

/**
 * POST /api/recurring/suggestions/{id}/accept — promote to a real
 * recurring_expenses row. Returns the new rule's id.
 */
export async function acceptSuggestion(id) {
  const headers = await authHeaders();
  const r = await axios.post(`${BACKEND_URL}/api/recurring/suggestions/${id}/accept`, {}, { headers, timeout: 15000 });
  return r.data?.recurring_id || null;
}

/**
 * POST /api/recurring/suggestions/{id}/dismiss — soft-dismiss. The
 * detector remembers the (kind, merchant_key) pair so it won't resurface.
 */
export async function dismissSuggestion(id) {
  const headers = await authHeaders();
  await axios.post(`${BACKEND_URL}/api/recurring/suggestions/${id}/dismiss`, {}, { headers, timeout: 15000 });
}

/**
 * Best-effort fire-and-forget scan trigger. Used after a successful
 * statement import so the user sees fresh suggestions on their next
 * Recurring page visit without any visible "scanning…" UI on the import
 * flow itself. Failures are swallowed.
 */
export function triggerScanInBackground() {
  scanForSuggestions().catch(() => { /* swallow */ });
}
